import React, { useEffect, useState } from "react";
import { Card, Statistic, Tooltip } from "antd";
import {
  InfoCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SettingOutlined,
  CloseOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { Column } from "@ant-design/plots";
import { BASE_URL } from "../../Components/Constant";
import ApiService from "../../Components/apiService.js";
import { useSelector } from "react-redux";

// Utility: Generate random soft color
function getRandomColor() {
  const colors = [
    "#FF6B6B", // Red
    "#FF8E53", // Orange
    "#FFCA28", // Yellow
    "#66BB6A", // Green
    "#26C6DA", // Cyan
    "#42A5F5", // Blue
    "#7E57C2", // Purple
    "#EC407A", // Pink
    "#8D6E63", // Brown
    "#26A69A", // Teal
    "#D4E157", // Lime
    "#AB47BC", // Violet
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

const MetricCard = ({
  metricObjectName,
  metricId,
  id,
  onRemove,
  disableRemove,
  onConfigure,
  label,
  objectToAggregate,
  isEditingWindow,
  fieldName,
  aggregationType,
  fieldType,
  rollupReturnType,
  fetchFields,
  showGraph,
  iconType,
  filters,
  logic,
}) => {
  const [data, setData] = useState([]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [change, setChange] = useState({ daily: 0, weekly: 0 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [objectId, setObjectId] = useState(null);
  const [randomColor, setRandomColor] = useState("#4A90E2");

  const defaultCurrency = useSelector((state) => state.data.orgItem);

  // Map icons dynamically, using random color
  const iconMap = {
    user: <UserOutlined style={{ fontSize: 20, color: randomColor }} />,
    users: <UsergroupAddOutlined style={{ fontSize: 20, color: randomColor }} />,
    cart: <ShoppingCartOutlined style={{ fontSize: 20, color: randomColor }} />,
    money: <DollarOutlined style={{ fontSize: 20, color: randomColor }} />,
    chart: <BarChartOutlined style={{ fontSize: 20, color: randomColor }} />,
  };

  function formatValue(value) {
    const currencySymbol = defaultCurrency?.currency || "";
    if (
      fieldType === "currency" ||
      rollupReturnType === "currency" ||
      (aggregationType === "sum" &&
        fieldName &&
        fieldName.toLowerCase().includes("amount"))
    ) {
      return `${currencySymbol}${Number(value).toLocaleString("en-US")}`;
    }
    return Number(value).toLocaleString("en-US");
  }

  useEffect(() => {
    // Pick random color once when card mounts
    setRandomColor(getRandomColor());
  }, []);

  useEffect(() => {
    fetchFields(metricObjectName, metricId);
  }, [metricId, metricObjectName]);

  useEffect(() => {
    if (
      !objectToAggregate ||
      !label ||
      !aggregationType ||
      (aggregationType !== "count" && !fieldName)
    ) {
      return;
    }
    setLoading(true);

    // Helper function to build dynamic filters payload
    const buildDynamicFilters = () => {
      // Check if filters exist and have valid data
      if (!filters || !Array.isArray(filters) || filters.length === 0) {
        return null;
      }

      // Filter out empty filters
      const validFilters = filters.filter(filter => 
        filter.field && filter.field.trim() !== '' && 
        (filter.value !== undefined && filter.value !== null && filter.value !== '')
      );

      if (validFilters.length === 0) {
        return null;
      }

      const dynamicFilters = {};
      validFilters.forEach((filter, index) => {
        // Convert string "true"/"false" to boolean
        let value = filter.value;
        if (typeof value === 'object' && value !== null) {
          // If value is an object (e.g., { $gt: "true" }), convert its property value
          const key = Object.keys(value)[0];
          let v = value[key];
          if (v === "true") v = true;
          if (v === "false") v = false;
          dynamicFilters[index + 1] = {
            field: filter.field,
            value: { [key]: v }
          };
        } else {
          if (value === "true") value = true;
          if (value === "false") value = false;
          dynamicFilters[index + 1] = {
            field: filter.field,
            value
          };
        }
      });
      // ...existing logic for filterLogic...
      let filterLogic = '';
      if (validFilters.length === 1) {
        filterLogic = '1';
      } else if (validFilters.length > 1) {
        filterLogic = logic || validFilters.map((_, index) => index + 1).join(' AND ');
      }
      return {
        dynamic_filter_logic: filterLogic,
        dynamic_filters: dynamicFilters
      };
    };

    const fieldsToAggregate =
      aggregationType === "count" ? { _id: "count" } : { [fieldName]: aggregationType };

    // Build the base payload
    const payload = {
      object_name: objectToAggregate,
      fields_to_aggregate: fieldsToAggregate,
      filters: {
        time_frames: [
          "daily",
          "all_records",
          "last_30_days",
          "last_7_days",
          "previous_day",
          "previous_week",
        ],
        date_field: "created_at",
      },
    };

    // Add dynamic filters if they exist
    const dynamicFilters = buildDynamicFilters();
    if (dynamicFilters) {
      payload.dynamic_filter_logic = dynamicFilters.dynamic_filter_logic;
      payload.dynamic_filters = dynamicFilters.dynamic_filters;
    }
    

    const apiServiceForObjectCount = new ApiService(
      `${BASE_URL}/aggregate_fields`,
      { "Content-Type": "application/json" },
      "POST",
      JSON.stringify(payload)
    );

    apiServiceForObjectCount
      .makeCall()
      .then((response) => {
        const dailyData = response?.daily || {};
        const weeklyData = response?.weekly || {};
        const monthlyData = response?.monthly || {};
        const allTimeData = response?.all_time || {};

        let valueKey = aggregationType === "count" ? "_id" : fieldName;

        setObjectId(response?.objectId);
        setData([
          { category: "Daily", value: dailyData?.[valueKey] || 0 },
          { category: "Weekly", value: weeklyData?.[valueKey] || 0 },
          { category: "Monthly", value: monthlyData?.[valueKey] || 0 },
        ]);
        setTotalWeight(allTimeData?.[valueKey] || 0);

        setChange({
          daily:
            (dailyData?.[valueKey] || 0) - (response?.previous_day?.[valueKey] || 0),
          weekly:
            (weeklyData?.[valueKey] || 0) - (response?.previous_week?.[valueKey] || 0),
        });
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [objectToAggregate, label, fieldName, aggregationType, filters, logic]);

  const chartConfig = {
    data,
    xField: "category",
    yField: "value",
    columnWidthRatio: 0.6,
    autoFit: true,
    color: randomColor,
    xAxis: {
      label: { style: { fontSize: 9 } },
      line: null,
      tickLine: null,
    },
    yAxis: {
      label: null,
      line: null,
      grid: null,
    },
    legend: false,
    tooltip: { showMarkers: false },
  };

  return (
    <Card
      style={{
        width: 260,
        borderRadius: 12,
        padding: 8,
        background: `linear-gradient(145deg, ${randomColor}20, white)`,
        border: `0.2px solid ${randomColor}`,            // thin same-color border
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",       // mild black shadow
      }}
      bodyStyle={{ padding: 12 }}
      loading={loading}
      className="metric-card"   // 👈 add a class for hover
      extra={isEditingWindow && (
        <>
          <SettingOutlined
            style={{ marginRight: 8, color: "#1890ff", cursor: "pointer" }}
            onClick={() => onConfigure?.(id)}
          />
          <CloseOutlined
            style={{
              color: disableRemove ? "#ccc" : "#f5222d",
              cursor: disableRemove ? "not-allowed" : "pointer",
            }}
            onClick={() => !disableRemove && onRemove(id)}
          />
        </>
      )}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {label}
          <Tooltip title={`View ${label}`}>
            <a href={`/object/${objectId}`} target="_blank" rel="noopener noreferrer">
              <InfoCircleOutlined />
            </a>
          </Tooltip>
        </div>
      }
    >
      {error ? (
        <div style={{ color: "red", fontSize: 12 }}>
          Error: {error?.message || error?.error}
        </div>
      ) : (
        <>
          {showGraph ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Statistic
                  value={totalWeight}
                  formatter={formatValue}
                  valueStyle={{
                    fontSize: 20,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
                <div style={{ marginTop: 4, fontSize: 12 }}>
                  <div>
                    {change.weekly >= 0 ? (
                      <div style={{ color: "green" }}>
                        Weekly <ArrowUpOutlined /> {formatValue(change.weekly)}
                      </div>
                    ) : (
                      <div style={{ color: "red" }}>
                        Weekly <ArrowDownOutlined /> {formatValue(change.weekly)}
                      </div>
                    )}
                  </div>
                  <div>
                    {change.daily >= 0 ? (
                      <div style={{ color: "green" }}>
                        Daily <ArrowUpOutlined /> {formatValue(change.daily)}
                      </div>
                    ) : (
                      <div style={{ color: "red" }}>
                        Daily <ArrowDownOutlined /> {formatValue(change.daily)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ width: 140, height: 160, flexShrink: 0 }}>
                <Column {...chartConfig} height={160} />
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 600 }}>{formatValue(totalWeight)}</div>
                <div style={{ fontSize: 12, color: change.daily >= 0 ? "green" : "red" }}>
                  {change.daily >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
                  {Math.abs(change.daily)} ({change.daily >= 0 ? "+" : ""}
                  {((change.daily / (totalWeight - change.daily || 1)) * 100).toFixed(0)}%)
                </div>
              </div>

              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${randomColor}`,
                }}
              >
                {iconMap[iconType] || iconMap["chart"]}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default MetricCard;
